import { useCallback, useEffect, useMemo, useState } from "react";
import type { EntityItemConfig, Id } from "@salestrak/ui";
import {
  useIssueGroupCounts,
} from "#app/hooks/issue-list/use-issue-group-counts";
import { useCountedGroupItems } from "#app/hooks/issue-list/use-counted-group-items";
import { useWorkflowStagesData } from "#app/lib/hooks/use-kanban-board-data";
import {
  useAllMemberItems,
  useTeamPool,
} from "#app/components/entity-selectors";
import { applyCurrentUserFilter } from "#app/lib/query-utils";
import {
  AGE_GROUPS,
  ISSUE_GROUP_SERVER_KEY,
  UNASSIGNED_DIMENSION_LABEL,
} from "#app/components/issues/issue-grouping";
import {
  buildBoardColumns,
  type BoardGroupDimension,
  type BoardColumnSource,
  type BuiltBoardColumn,
} from "#app/components/issues/board-columns";
import type { ViewConfig } from "#app/lib/view-config";
import { type WorkspaceWorkflow as Workflow } from "#app/lib/data";

export type Stage = ReturnType<typeof useWorkflowStagesData>["stages"][number];
export type UserItem = EntityItemConfig;
export type TeamItem = ReturnType<typeof useTeamPool>["pool"]["items"][number];

// A column's header payload — resolved by dimension. `null` for the synthetic
// unassigned bucket; the age dimension carries its status key as a string.
export type BoardColumnData = Stage | Workflow | UserItem | TeamItem | string;
export type BoardColumn = BuiltBoardColumn<BoardColumnData>;

/**
 * The board's columns for the active dimension: the sources per dimension,
 * the per-column counts that gate the unbounded dimensions, and the collapsed
 * set (a person's collapses plus the empty stages the view auto-collapses).
 */
export function useIssuesBoardColumns({
  groupBy,
  groupSortDirection,
  config,
  setConfig,
  base,
  isCurrentUserFilterApplied,
  stages,
  workflows,
}: {
  groupBy: BoardGroupDimension;
  groupSortDirection: ViewConfig["groupDirection"];
  config: ViewConfig;
  setConfig: (patch: Partial<ViewConfig>) => void;
  base: Record<string, unknown> | undefined;
  isCurrentUserFilterApplied: boolean | undefined;
  stages: Stage[];
  workflows: readonly Workflow[];
}) {
  const roster = useAllMemberItems();
  const { baseItems: teamBaseItems } = useTeamPool();

  // --- Column counts ---
  // The unbounded dimensions always need them (only non-empty buckets render);
  // the stage dimension only while "Collapse empty stages" has to know which
  // columns are empty.
  const needsCounts =
    groupBy === "assignee" ||
    groupBy === "team" ||
    groupBy === "teams" ||
    (groupBy === "stage" && config.collapseEmptyStages);
  const countsWhere = useMemo(
    () =>
      needsCounts
        ? applyCurrentUserFilter(base, isCurrentUserFilterApplied ?? false)
        : undefined,
    [needsCounts, base, isCurrentUserFilterApplied],
  );
  const { counts, ready: countsReady } = useIssueGroupCounts({
    where: countsWhere,
    groupBy: needsCounts ? ISSUE_GROUP_SERVER_KEY[groupBy] : undefined,
  });
  // Every narrowing (search, a filter, Only-Me) refetches the counts, and
  // `ready` drops while the new answer is in flight. Only the FIRST answer
  // per dimension gates the board: after it the board keeps rendering on the
  // previous counts until the next lands, so the toolbar — and the search
  // field a person is typing into — never unmounts mid-narrowing. A change
  // of dimension does wait again: the previous answer is keyed by the other
  // dimension's buckets and would render an empty board for a frame.
  const [countsSeenFor, setCountsSeenFor] = useState<string | null>(null);
  useEffect(() => {
    if (countsReady) setCountsSeenFor(groupBy);
  }, [countsReady, groupBy]);
  const countsSettled = countsReady || countsSeenFor === groupBy;

  const { memberItems: allMembers, teamItems } = useCountedGroupItems(
    groupBy,
    counts,
    roster,
    teamBaseItems,
  );

  // --- Column sources per dimension ---
  const stageSources = useMemo<BoardColumnSource<BoardColumnData>[]>(
    () =>
      (stages as Stage[]).map((stage) => ({
        key: stage.stageId,
        label: stage.name,
        data: stage,
      })),
    [stages],
  );
  const workflowSources = useMemo<BoardColumnSource<BoardColumnData>[]>(
    () =>
      workflows.map((w) => ({
        key: w.workflowId,
        label: w.name ?? "Untitled workflow",
        data: w,
      })),
    [workflows],
  );
  const userSources = useMemo<BoardColumnSource<BoardColumnData>[]>(
    () => allMembers.map((u) => ({ key: u.id, label: u.title, data: u })),
    [allMembers],
  );
  const teamSources = useMemo<BoardColumnSource<BoardColumnData>[]>(
    () => teamItems.map((t) => ({ key: t.id, label: t.title, data: t })),
    [teamItems],
  );
  const ageSources = useMemo<BoardColumnSource<BoardColumnData>[]>(
    () => AGE_GROUPS.map((g) => ({ key: g.key, label: g.label, data: g.key })),
    [],
  );

  const columns = useMemo<BoardColumn[]>(() => {
    switch (groupBy) {
      case "stage":
        return buildBoardColumns({
          dimension: "stage",
          sources: stageSources,
          counts,
          sortDirection: groupSortDirection,
          unassignedLabel: UNASSIGNED_DIMENSION_LABEL.stage,
        });
      case "workflow":
        return buildBoardColumns({
          dimension: "workflow",
          sources: workflowSources,
          counts,
          sortDirection: groupSortDirection,
          unassignedLabel: UNASSIGNED_DIMENSION_LABEL.workflow,
        });
      case "age":
        return buildBoardColumns({
          dimension: "age",
          sources: ageSources,
          counts,
          sortDirection: groupSortDirection,
          unassignedLabel: UNASSIGNED_DIMENSION_LABEL.age,
        });
      case "assignee":
        return buildBoardColumns({
          dimension: "assignee",
          sources: userSources,
          counts,
          sortDirection: groupSortDirection,
          unassignedLabel: UNASSIGNED_DIMENSION_LABEL.assignee,
        });
      case "team":
        return buildBoardColumns({
          dimension: "team",
          sources: teamSources,
          counts,
          sortDirection: groupSortDirection,
          unassignedLabel: UNASSIGNED_DIMENSION_LABEL.team,
        });
      case "teams":
        return buildBoardColumns({
          dimension: "teams",
          sources: teamSources,
          counts,
          sortDirection: groupSortDirection,
          unassignedLabel: UNASSIGNED_DIMENSION_LABEL.teams,
        });
    }
  }, [
    groupBy,
    stageSources,
    workflowSources,
    ageSources,
    userSources,
    teamSources,
    counts,
    groupSortDirection,
  ]);

  // Collapsed columns ARE view config: the column-header collapse drafts
  // through the same channel as the panel's controls, so collapsing a column
  // raises Save. Sorted so the same set always dirty-compares identically.
  // Keys are the kanban column ids (`getColumnId`), the same ids the board
  // reports back from a toggle.
  //
  // Two sets, one per direction. `collapsedGroups` holds the columns a person
  // closed. "Collapse empty stages" closes every empty stage column on its
  // own, so opening one of THOSE lands in `expandedGroups` instead — the
  // override outlives a reload, and the column reads collapsed again the
  // moment the toggle is switched off and back on with the override cleared.
  // Auto-collapse waits for the counts: before they land every column reads
  // empty, and the board would open collapsed only to unfold a beat later.
  const autoCollapsesEmpty = groupBy === "stage" && config.collapseEmptyStages;
  const isEmptyStageColumn = useCallback(
    (column: BoardColumn) =>
      autoCollapsesEmpty &&
      countsSettled &&
      column.dimension === "stage" &&
      !column.isUnassigned &&
      (counts[column.key] ?? 0) === 0,
    [autoCollapsesEmpty, countsSettled, counts],
  );
  const collapsedColumnIds = useMemo(() => {
    const expanded = new Set(config.expandedGroups);
    const set = new Set<Id>(config.collapsedGroups);
    for (const column of columns) {
      if (isEmptyStageColumn(column) && !expanded.has(column.id)) {
        set.add(column.id);
      }
    }
    return set;
  }, [columns, config.collapsedGroups, config.expandedGroups, isEmptyStageColumn]);
  const handleToggleColumnCollapse = useCallback(
    (id: Id) => {
      const key = String(id);
      const column = columns.find((c) => c.id === key);
      const auto = column ? isEmptyStageColumn(column) : false;
      const collapsed = new Set(config.collapsedGroups);
      const expanded = new Set(config.expandedGroups);
      if (collapsedColumnIds.has(id)) {
        // Opening: drop any explicit collapse; an auto-collapsed column also
        // needs the override, or the counts would close it again.
        collapsed.delete(key);
        if (auto) expanded.add(key);
      } else {
        // Closing: an auto-collapsed column only has to lose its override;
        // any other column is an explicit collapse.
        expanded.delete(key);
        if (!auto) collapsed.add(key);
      }
      setConfig({
        collapsedGroups: [...collapsed].sort(),
        expandedGroups: [...expanded].sort(),
      });
    },
    [
      collapsedColumnIds,
      columns,
      config.collapsedGroups,
      config.expandedGroups,
      isEmptyStageColumn,
      setConfig,
    ],
  );

  // A drop onto a collapsed rail opens the column, but it is not a person
  // deciding the column should stay open. An auto-collapsed (empty) stage
  // needs no override at all: the drop makes it non-empty, and the refetched
  // counts open it. A column a person closed by hand just loses that collapse.
  const handleDropIntoCollapsedColumn = useCallback(
    (id: Id) => {
      const key = String(id);
      const column = columns.find((c) => c.id === key);
      if (column && isEmptyStageColumn(column)) return;
      if (!config.collapsedGroups.includes(key)) return;
      setConfig({
        collapsedGroups: config.collapsedGroups.filter((k) => k !== key),
      });
    },
    [columns, config.collapsedGroups, isEmptyStageColumn, setConfig],
  );

  // A "keep open" override only means something while its stage is empty.
  // Once the stage holds a card the counts keep it open on their own, so the
  // override is dropped — and the stage collapses again the next time it
  // empties, which is what the toggle promised.
  useEffect(() => {
    if (!autoCollapsesEmpty || !countsSettled) return;
    const stale = config.expandedGroups.filter((key) => {
      const column = columns.find((c) => c.id === key);
      return (
        column != null &&
        column.dimension === "stage" &&
        !column.isUnassigned &&
        (counts[column.key] ?? 0) > 0
      );
    });
    if (stale.length === 0) return;
    setConfig({
      expandedGroups: config.expandedGroups.filter((k) => !stale.includes(k)),
    });
  }, [autoCollapsesEmpty, countsSettled, columns, counts, config.expandedGroups, setConfig]);

  return {
    counts,
    needsCounts,
    countsSettled,
    columns,
    collapsedColumnIds,
    handleToggleColumnCollapse,
    handleDropIntoCollapsedColumn,
  };
}
